import { useState } from 'react';
import { useAtom } from 'jotai';
import type {
  PreDrillTargetZoneIntervalRow,
  PreDrillVerificationItems,
  PreDrillWorkspace,
} from 'types/api-responses';
import type { ApiPayloads } from 'types/api-payloads';
import {
  clearTargetZoneAtom,
  confirmTargetZoneAtom,
  preDrillVerificationEventAtom,
  targetZoneRefreshAtom,
} from './atoms';
import type { TargetZoneHrefs } from './utils';

type TargetZoneConfirmationAttributes =
  ApiPayloads['confirmPreDrillTargetZone']['payload']['target_zone_confirmation'];

interface UseTargetZoneActionsArgs {
  workspace: PreDrillWorkspace;
  hrefs: TargetZoneHrefs;
  /** The target-zone verification item, whose verdict Mark as Correct also flips. */
  targetZoneItem: PreDrillVerificationItems[number] | null;
  /** What a confirmation says about an untouched survey; null where there is none. */
  rebuildSurvey: boolean | null;
  surveyUntouched: boolean;
  addPendingPushEvent: (event: string) => void;
  clearGenerationError: () => void;
  refetchShow: () => void;
  refetchItems: () => void;
  refetchSuggestions: () => void;
  refreshCharts: () => void;
  /** Change Match saved: the drawer closes. */
  onManualSelected: () => void;
}

/**
 * The Target Zone section's writes: re-run, confirm (Mark as Correct and Change Match),
 * remove the verdict and clear the selection, with their loading and failure state.
 * @param {UseTargetZoneActionsArgs} args The workspace, its addresses and callbacks.
 * @return {object} The handlers and their state.
 */
export const useTargetZoneActions = ({
  workspace,
  hrefs,
  targetZoneItem,
  rebuildSurvey,
  surveyUntouched,
  addPendingPushEvent,
  clearGenerationError,
  refetchShow,
  refetchItems,
  refetchSuggestions,
  refreshCharts,
  onManualSelected,
}: UseTargetZoneActionsArgs) => {
  const [confirmState, confirmTargetZone] = useAtom(confirmTargetZoneAtom);
  const [clearState, clearTargetZone] = useAtom(clearTargetZoneAtom);
  const [rerunState, rerunAgent] = useAtom(targetZoneRefreshAtom);
  const [verdictState, postVerificationEvent] = useAtom(
    preDrillVerificationEventAtom
  );
  // The confirm atom is a singleton, so its last error would carry over; own the
  // error display locally so it starts clean on each workspace entry.
  const [confirmFailed, setConfirmFailed] = useState(false);
  // Clear Selection (DELETE) failure, surfaced as its own banner.
  const [clearFailed, setClearFailed] = useState(false);

  // Re-run agent (202): the `target_zone_generation` push drives the single
  // refresh — no onSuccess refetch, so work isn't fetched twice.
  const handleRerunAgent = () => {
    clearGenerationError();
    rerunAgent({
      type: 'POST',
      url: hrefs.refreshes,
      data: {},
      // Subscribe to the target-zone push immediately + re-fetch the show.
      onSuccess: () => {
        addPendingPushEvent('target_zone_generation');
        refetchShow();
      },
    });
  };

  // Shared by Mark as Correct and Change Match. It arms the geometry push for the
  // rebuild it cascades, except on an untouched survey the backend keeps.
  const postTargetZoneConfirmation = (
    attributes: TargetZoneConfirmationAttributes,
    onSubmitted: () => void
  ) => {
    setConfirmFailed(false);
    confirmTargetZone({
      type: 'POST',
      url: hrefs.confirmations,
      data: {
        target_zone_confirmation: {
          ...attributes,
          ...(rebuildSurvey === null ? {} : { rebuild_survey: rebuildSurvey }),
        },
      },
      onSuccess: () => {
        if (!surveyUntouched || rebuildSurvey) {
          addPendingPushEvent('geometry_construction');
        }
        onSubmitted();
      },
      onError: () => setConfirmFailed(true),
    });
  };

  // Mark as Correct: confirm the interval.
  const handleConfirmTargetZone = (
    geologicIntervalId: number,
    manualOverride: boolean
  ) => {
    postTargetZoneConfirmation(
      {
        geologic_interval_id: geologicIntervalId,
        // Carry the picked tile's source: a manual override vs an AI selection.
        // The note belongs only to a manual pick, so it's cleared on AI confirm.
        manual_override: manualOverride,
        manual_note: manualOverride ? workspace.targetZoneManualNote : null,
      },
      () => {
        // Confirming must also post `verification_correct` (same event the Tasks
        // drawer uses) — this flips `correctnessState`; the POST alone doesn't.
        if (targetZoneItem) {
          postVerificationEvent({
            type: 'POST',
            url: `/verification_items/${targetZoneItem.id}/events`,
            data: { event: { kind: 'verification_correct' } },
            onSuccess: () => {
              refetchShow();
              refetchItems();
              refreshCharts();
            },
            onError: () => setConfirmFailed(true),
          });
        } else {
          refetchShow();
          refetchItems();
          refreshCharts();
        }
      }
    );
  };

  // Remove Correct Status: revert the target-zone verdict, then re-fetch so the
  // toggle + task bar reflect it.
  const handleRemoveTargetZoneCorrect = () => {
    if (!targetZoneItem) return;
    postVerificationEvent({
      type: 'POST',
      url: `/verification_items/${targetZoneItem.id}/events`,
      data: { event: { kind: 'verification_reverted' } },
      onSuccess: () => {
        refetchItems();
        refetchShow();
      },
    });
  };

  // Change Match: commit a manually-picked interval with its collection context — a
  // collection row commits that collection, a loose-grid row clears it, and a
  // bare-interval row leaves it untouched.
  const handleManualSelect = (row: PreDrillTargetZoneIntervalRow) => {
    postTargetZoneConfirmation(
      {
        geologic_interval_id: row.id,
        manual_override: true,
        manual_note: 'Manually selected from the Geologic Interval catalog.',
        ...(row.structureGridCollectionId !== null
          ? { structure_grid_collection_id: row.structureGridCollectionId }
          : row.structureGridId !== null
            ? { clear_structure_grid_collection: true }
            : {}),
      },
      () => {
        onManualSelected();
        refetchShow();
        refetchSuggestions();
        refetchItems();
        // No chart refresh: the armed geometry push redraws them after the rebuild.
      }
    );
  };

  // Clear Selection: DELETE the target-zone result, returning to neutral (BE
  // resets geometry + reverts verdicts).
  const handleClearSelection = () => {
    setClearFailed(false);
    clearTargetZone({
      type: 'DELETE',
      url: hrefs.suggestions,
      data: {},
      onSuccess: () => {
        refetchShow();
        refetchSuggestions();
        refetchItems();
        refreshCharts();
      },
      onError: () => setClearFailed(true),
    });
  };

  return {
    confirmState,
    clearState,
    rerunState,
    verdictState,
    confirmFailed,
    clearFailed,
    handleRerunAgent,
    handleConfirmTargetZone,
    handleRemoveTargetZoneCorrect,
    handleManualSelect,
    handleClearSelection,
  };
};
